import {Component, OnDestroy, OnInit, AfterViewInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
// ReactiveX for JavaScript
import {EMPTY, Observable, Subject, Subscription, forkJoin, of} from 'rxjs';
import {catchError, map, switchMap, takeUntil} from 'rxjs/operators';
// Interfaces für Structured Objects einbinden und Service des globalen Regionsfilters
import {GlobalRegionsfilterService} from '@global/components/global-regionsfilter/global-regionsfilter.service';
import {UserSettingsService} from '@global/services/user-settings.service';
import {InstitutionsService} from '../institutions.service';

// Services für Events
import {EventsService} from '@modules/events/events.service';
import {GridService} from '@shared/grid/grid.service';
import {InstitutionsEventsListService} from '../institutions-events-list/institutions-events-list.service';

// Service von Shared Modules
import {ToolbarService} from '@shared/toolbar/toolbar.service';
// Interfaces für Structured Objects einbinden
import {CWEvent} from '@shared/cw-event';
import {PopupConfirmationComponent} from '@shared/popups/popup-confirmation/popup-confirmation.component';
import {PopupMessageComponent} from '@shared/popups/popup-message/popup-message.component';

// Forms
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

// Services
import {EventsDataService} from '@modules/events/events-data/events-data.service';
import {EventsPermissionsService} from '@shared/events/events-permissions.service';
import {UserPermissionsService} from '@global/services/user-permissions.service';
import {EventsBudgetAssignmentService} from '@modules/events/events-budget-assignment/events-budget-assignment.service';
// eslint-disable-next-line max-len
import {CWResult} from '@shared/cw-result';
import {SelectData} from '@shared/select-data';

// Environment
import {environment} from '@environment';
import {hasOwn} from '@shared/utils';

@Component({
    selector: 'phscw-institutions-events',
    templateUrl: './institutions-events.component.html',
    styleUrls: ['./institutions-events.component.scss'],
    standalone: false,
})
export class InstitutionsEventsComponent implements OnInit, OnDestroy {
    // Referenzen auf Subject-Subscriptions
    private _componentDestroyed$ = new Subject<void>();
    private _subscriptions = new Subscription();

    // Flag definiert, ob Löschen von Veranstaltungen erlaubt ist
    allowDeleteEvents = false;

    // Service-Referenzen für Events hinzufügen

    // Grundlage des E-Detailer-Links
    edetailerBaseLink = '';
    // E-Detailer-Link
    edetailerLink = '';

    // Division-Defaultmäßig auf 1 setzen, wenn z.
    division: number = null;

    // ID der aktuell ausgewählten Einrichtung
    institutionId: number;
    // ID des aktuell ausgewählten Events
    eventId: number;
    // Zum Wechsel von der Listen-Ansicht in die Eventdetailsansicht
    showEventDetails = false;

    // Modul-Daten (Alle Events der Einrichtung)
    data: any[] = [];

    // Ausgewählte Event-Daten für die Bearbeitung
    selectedEventData: any = null;

    // Per Checkbox markierte Events (für Löschen)
    checkedEvents: any[] = [];

    // Bearbeitungsberechtigung basierend auf Workflow-Permissions
    editAllowed = true;

    // Grid columns definition
    gridColumns: any[] = [
        {
            columnDef: 'event_name',
            header: 'Name',
            cell: (element: any) => `${element.name1 || ''} ${element.name2 || ''}`,
            formatWidth: '300px',
            tooltip: true,
        },
        {
            columnDef: 'event_type',
            header: 'Maßnahme',
            cell: (element: any) => `${element.event_type_label || ''}`,
        },
        {
            columnDef: 'event_subtype',
            header: 'Investition',
            cell: (element: any) => `${element.event_subtype_label || ''}`,
            formatWidth: '120px',
        },
        {
            columnDef: 'event_status',
            columnField: 'event_status',
            header: 'Status',
            formatTemplate: 'listentries',
            listentry: 'eventStatus',
            formatWidth: '120px',
        },
        {
            columnDef: 'event_location',
            header: 'Ort',
            cell: (element: any) => `${element.zipcode || ''} ${element.city || ''}`,
            formatWidth: '120px',
        },
        {
            columnDef: 'event_start',
            header: 'Start',
            cell: (element: any) =>
                element.event_start ?
                    new Date(element.event_start).toLocaleDateString('de-DE') :
                    '',
            formatWidth: '120px',
        },
        {
            columnDef: 'event_end',
            header: 'Ende',
            cell: (element: any) =>
                element.event_end ?
                    new Date(element.event_end).toLocaleDateString('de-DE') :
                    '',
            formatWidth: '120px',
        },
        {
            columnDef: 'event_cost_type_plan',
            columnField: 'event_cost_type_plan',
            header: 'Kostenart Plan-Kosten',
            formatTemplate: 'listentries',
            listentry: 'eventCostType',
            formatWidth: '120px',
        },
        {
            columnDef: 'event_cost_type_actual',
            columnField: 'event_cost_type_actual',
            header: 'Kostenart Ist-Kosten',
            formatTemplate: 'listentries',
            listentry: 'eventCostType',
            formatWidth: '120px',
        },
        {
            columnDef: 'cost_plan',
            header: 'Soll-Kosten',
            cell: (element: any) => `${element.cost_plan || ''}`,
            formatWidth: '120px',
            formatTemplate: 'currency',
        },
        {
            columnDef: 'cost_actual',
            header: 'Ist-Kosten',
            cell: (element: any) => `${element.cost_actual || ''}`,
            formatWidth: '120px',
            formatTemplate: 'currency',
        },
        {
            columnDef: 'brand',
            header: 'Produkt',
            cell: (element: any) => `${element.brand || ''}`,
            formatWidth: '140px',
        },
        {
            columnDef: 'budget_region',
            header: 'Gebiet',
            cell: (element: any) => `${element.region_label || element.region_id || ''}`,
            formatWidth: '200px',
        },
        {
            columnDef: 'budget_type',
            columnField: 'budget_type',
            header: 'Budgetart',
            formatTemplate: 'listentries',
            listentry: 'budgetType',
            formatWidth: '120px',
        },
    ];

    // Grid displayed columns
    gridDisplayedColumns = [
        'select',
        'event_name',
        'event_type',
        'brand',
        'event_status',
        'event_start',
        'event_end',
        'event_cost_type_plan',
        'cost_plan',
        'event_cost_type_actual',
        'cost_actual',
        'budget_region',
        'budget_type',
    ];

    // Form group for event creation
    eventForm: UntypedFormGroup;

    // Selected institution name
    selectedInstitutionName: string;

    constructor(
        private globalRegionsfilterService: GlobalRegionsfilterService,
        private userSettingsService: UserSettingsService,
        private toolbarService: ToolbarService,
        private institutionsService: InstitutionsService,
        private eventsService: EventsService,
        private gridService: GridService,
        private institutionsEventsListService: InstitutionsEventsListService,
        private formBuilder: UntypedFormBuilder,
        private eventsDataService: EventsDataService,
        private eventsPermissionsService: EventsPermissionsService,
        private dialog: MatDialog,
        private budgetAssignmentService: EventsBudgetAssignmentService,
        private userPermissionsService: UserPermissionsService,
    ) {
        this.eventForm = this.createEventForm();
    }



    private createEventForm(): UntypedFormGroup {
        return this.formBuilder.group({
            institution_name: ['', Validators.required],
            event_type: [''],
            event_subtype: [''],
            event_status: [''],
            event_start_date: [''],
            brand: [''],
            event_cost_type_plan: [''],
            cost_plan: [''],
            event_cost_type_actual: [''],
            cost_actual: [''],
            event_notes: [''],
        });
    }

    ngOnInit() {
        if (hasOwn(environment, 'edetailerLink')) {
            this.edetailerBaseLink = environment['edetailerLink'];
        }
        // Gewählte Division nehmen und als Start-Parameter mitgeben.
        this.division = this.userSettingsService.getValue('salesRegionsfilterDivision');
        // Link zum E-Detailer mit Parameter für Division anreichern.
        this.edetailerLink = this.edetailerBaseLink + 'start/' + this.division;

        // Berechtigung "allowDeleteEvents" prüfen
        this.checkAllowDeleteEvents();

        // Subscriptions initiieren
        this.initializeSubscriptions();

        // Toolbar-Events subscriben
        this.initializeEventSubscriptions();

        // Load the currently selected institution if available
        if (this.institutionsService.selectedInstitution) {
            this.institutionId = this.institutionsService.selectedInstitution.id;
            this.selectedInstitutionName = this.institutionsService.selectedInstitution.name1 || '';
            // Update the form with the selected institution
            this.eventForm.patchValue({
                institution_name: this.selectedInstitutionName,
            });
            // Disable the field to make it readonly
            this.eventForm.get('institution_name')?.disable();
        }
    }

    ngOnDestroy() {
        this._componentDestroyed$.next();
        this._componentDestroyed$.complete();
        this._subscriptions.unsubscribe();
    }

    /**
     * Subscibtions erstellen
     */
    initializeSubscriptions() {
        // Wenn Regionsfilter geändert wird
        this.globalRegionsfilterService.eventRegionsfilterChanged
            .pipe(takeUntil(this._componentDestroyed$))
            .subscribe((result: CWEvent) => {
                // Event-Daten
                const event: CWEvent = result;
                // Abbruch, falls Regionsfilter nicht für Umsatzanalyse (oder Produktanalyse) gilt
                if (event.target !== 'institutions') {
                    return;
                }
                this.division = event.data['division'];
                this.edetailerLink = this.edetailerBaseLink + 'start/' + this.division;
            });

        this.institutionsService.selectionChanged
            .pipe(takeUntil(this._componentDestroyed$))
            .subscribe((result: number) => {
                // ID des Eintrags zwischenspeichern
                this.onSelectionChanged(result);
                // Events laden, wenn die Liste sichtbar ist
                this.loadEventsIfVisible();
            });
    }

    // Wenn ein neuer Event (über Toolbar) angelegt werden soll...
    initializeEventSubscriptions() {
        // Wenn ein neuer Event (über Toolbar) angelegt werden soll...
        this._subscriptions.add(
            this.toolbarService.eventAddItem.subscribe((result) => {
                this.onEventAddItem(result);
            }),
        );

        // Event der Toolbar zum Löschen einer Veranstaltung
        this._subscriptions.add(
            this.toolbarService.eventDeleteItem.subscribe((event: CWEvent) => {
                if (event.target !== 'institutions-events') {
                    return;
                }
                this.openDeleteDialog();
            }),
        );

        // Event "eventGridSelectionChanged" von "gridService" abonnieren
        this._subscriptions.add(
            this.gridService.eventGridSelectionChanged
                .pipe(takeUntil(this._componentDestroyed$))
                .subscribe((event: CWEvent) => {
                    // Abbruch, falls das Event nicht vom richtigen Grid kommt
                    if (event.sender !== 'institutionsEvents') {
                        return;
                    }
                    this.onEventGridSelectionChanged(event);
                }),
        );

        // Checkbox-Auswahl im Grid abonnieren
        this._subscriptions.add(
            this.gridService.eventGridCheckboxClicked
                .pipe(takeUntil(this._componentDestroyed$))
                .subscribe((event: CWEvent) => {
                    if (event.sender !== 'institutionsEvents') {
                        return;
                    }
                    this.onEventGridCheckboxClicked(event);
                }),
        );
    }



    private fillEventForm(event: any) {
        this.eventForm.patchValue({
            institution_name: this.selectedInstitutionName,
            event_type: event.event_type,
            event_subtype: event.event_subtype,
            event_status: event.event_status,
            event_start_date: event.event_start,
            brand: event.brand,
            event_cost_type_plan: event.event_cost_type_plan,
            event_cost_type_actual: event.event_cost_type_actual,
            cost_plan: event.cost_plan,
            cost_actual: event.cost_actual,
            event_notes: event.event_notes,
        });

        // Institution Name Feld aktualisieren und deaktivieren, da es nicht geändert werden soll
        this.eventForm.get('institution_name')?.setValue(this.selectedInstitutionName);
        this.eventForm.get('institution_name')?.disable();
    }

    /**
     * @param id
     * @brief   Auf Wechsel der Einrichtung reagieren
     * @author  Tobias Hannemann <t.hannemann@pharmakon.software>
     */
    onSelectionChanged(id: number): void {
        // Bereits vorhandene Daten (einer anderen Einrichtung) entfernen
        this.resetData();
        // ID der aktuellen Einrichtung merken
        this.institutionId = id;
        // Aus dem Detail-Modus von Events herauswechseln
        this.showEventDetails = false;
        // Selektion zurücksetzen
        this.eventId = 0;

        // If we have an institution ID, try to get its name
        if (id && id > 0) {
            // Fetch the institution details to get the name
            this.loadInstitutionName(id);
        }
    }

    // Alle Events der Einrichtung laden
    loadData(): void {
        if (this.institutionId) {
            const currentInstitutionId = this.institutionId;
            this.institutionsEventsListService.loadInstitutionEvents(currentInstitutionId).pipe(
                switchMap((result: CWResult) => {
                    const rows = result?.data as Record<string, unknown>[];
                    if (!rows?.length) {
                        return of([]);
                    }
                    // Prüfen ob Einrichtung noch aktuell ist
                    if (rows[0]['institution_id'] && rows[0]['institution_id'] !== currentInstitutionId) {
                        return of(null);
                    }
                    // Budget-Zuordnung parallel für alle Events laden und mergen
                    return forkJoin(
                        rows.map((event) =>
                            this.budgetAssignmentService.loadEventBudgetAssignments(event['id'] as number).pipe(
                                map((budgetResult: CWResult) => {
                                    type BudgetData = {assignments?: {region_label?: string; budget_type?: number}[]};
                                    const assignment = (budgetResult?.data as BudgetData)?.assignments?.[0];
                                    return {
                                        ...event,
                                        region_label: assignment?.region_label ?? '',
                                        budget_type: assignment?.budget_type ?? '',
                                    };
                                }),
                            ),
                        ),
                    );
                }),
            ).subscribe({
                next: (events) => {
                    if (events) {
                        this.data = events;
                    }
                },
            });
        }
    }

    // Daten zurücksetzen (z.B. wenn Einrichtung gewechselt wird)
    resetData(): void {
        this.data = [];
    }

    // Daten laden, wenn Events-Liste sichtbar ist
    loadEventsIfVisible(): void {
        if (!this.showEventDetails && this.institutionId) {
            this.loadData();
        }
    }

    private loadInstitutionName(id: number) {
        // Use the selected institution from the service if available
        if (this.institutionsService.selectedInstitution && this.institutionsService.selectedInstitution.id === id) {
            const institution = this.institutionsService.selectedInstitution;
            if (institution.name1) {
                this.selectedInstitutionName = institution.name1;
                // Only update the form if we're showing the event details and the field is enabled
                if (this.showEventDetails && this.eventForm.get('institution_name')?.enabled) {
                    this.eventForm.patchValue({
                        institution_name: institution.name1,
                    });
                }
            }
        }
    }

    // Auf Event "eventGridSelectionChanged" von "gridService" reagieren
    onEventGridSelectionChanged(result) {
        const event: CWEvent = result;

        if (event.sender == 'institutionsEvents') {
            this.eventId = event.data['selectedRow']['id'];
            this.selectedEventData = event.data['selectedRow'];

            // Permissions vom Backend laden, um zu prüfen ob Bearbeitung erlaubt ist
            this.loadPermissionsAndShowDetails();
        }
    }

    private checkAllowDeleteEvents(): void {
        this.allowDeleteEvents = this.userPermissionsService.getPermissionValue('allowDeleteEvents');
    }

    /**
     * Lädt die Workflow-Permissions für das ausgewählte Event und zeigt die Details an.
     * Ob bearbeitet werden darf, wird dynamisch über den Workflow-Status bestimmt – kein Hardcoding nötig.
     */
    private loadPermissionsAndShowDetails() {
        // Für neue Events ist Bearbeitung immer erlaubt
        if (!this.eventId || this.eventId === 0) {
            this.editAllowed = true;
            this.showEventDetails = true;
            return;
        }

        this.eventsPermissionsService.getAllPermissionsRequest(this.eventId).subscribe((result) => {
            if (result.success && result.data?.permissions) {
                const permissions = result.data.permissions;

                /**
                 * Prüfe ob der User dieses Event bearbeiten darf (basierend auf Workflow-State + Rolle).
                 * Default ist erlaubt – nur wenn explizit false gesetzt, wird gesperrt.
                 */
                const editOwn = permissions['allowEditOwnEventData'];
                const editOther = permissions['allowEditOtherEventData'];

                if (editOwn || editOther) {
                    // Permissions existieren → nur erlaubt wenn mindestens eine true ist
                    this.editAllowed = editOwn?.value === true || editOther?.value === true;
                } else {
                    // Keine Permissions konfiguriert → Bearbeitung erlaubt
                    this.editAllowed = true;
                }
            } else {
                // Keine Permissions geladen → Bearbeitung erlaubt (kein Workflow konfiguriert)
                this.editAllowed = true;
            }

            this.showEventDetails = true;

            // Form mit den ausgewählten Event-Daten füllen
            if (this.selectedEventData) {
                this.fillEventForm(this.selectedEventData);
            }
        });
    }

    // Auf Toolbar-Klick reagieren (Event anlegen)
    onEventAddItem(result) {
        const event: CWEvent = result;
        if (event.target == 'institutions-events') {
            this.showEventDetails = true;
            this.editAllowed = true; // Neue Events sind immer bearbeitbar
            // Neue Event-Erstellung - keine vorhandenen Daten
            this.selectedEventData = null;
            this.selectedInstitutionName = '';

            // Pre-fill the institution name if we have a selected institution
            if (this.institutionId && this.institutionsService.selectedInstitution) {
                this.selectedInstitutionName = this.institutionsService.selectedInstitution.name1 || '';
            }
        }
    }

    onInstitutionSelected(event: any) {
        this.selectedInstitutionName = event.optionValue;
        this.eventForm.get('institution_name')?.setValue(this.selectedInstitutionName);
        // Disable the field after selection to make it readonly
        this.eventForm.get('institution_name')?.disable();
    }

    onInstitutionCleared() {
        this.selectedInstitutionName = '';
        this.eventForm.get('institution_name')?.setValue('');
        this.eventForm.get('institution_name')?.enable();
    }

    onSave() {
        if (this.eventForm.invalid) {
            return;
        }

        // Get form values
        const formData = this.eventForm.value;

        // Prepare the event data to be sent to the backend
        const eventData = {
            institution_id: this.institutionId,
            event_type: formData.event_type,
            event_subtype: formData.event_subtype,
            event_status: formData.event_status,
            event_start: formData.event_start_date,
            brand: formData.brand,
            cost_plan: formData.cost_plan,
            cost_actual: formData.cost_actual,
            event_cost_type_plan: formData.event_cost_type_plan,
            event_cost_type_actual: formData.event_cost_type_actual,
            event_notes: formData.event_notes,
        };

        /*
         * Call the backend service to save the event
         * We need to use the EventsDataService to create a new event
         * Since we're creating a new event, we'll pass 0 as the event ID
         */
        this.eventsDataService.saveDataInstitutions(0, eventData).subscribe(
            (result: any) => {
                if (result.success) {
                    const eventId = result.data.id;

                    // Now handle the additional requirements
                    this.handleEventCreationRequirements(eventId, formData);

                    // Handle successful creation
                    this.showEventDetails = false;
                    /**
                     * Optionally refresh the events list
                     * Refresh the events list by triggering a reload
                     */
                    this.toolbarService.eventAddItem.next({
                        sender: 'institutions-events',
                        target: 'institutions-events',
                        data: {success: true, event: result.data},
                    });

                    this.closeEventForm();
                } else if (!result.success && result.message.includes('Kein Budget')) {
                    this.showBudgetMissing();
                } else {
                    // Handle error
                    console.error('Error creating event:', result.message);
                }

                // Grid aktualisieren, um neue Daten anzuzeigen
                this.refreshGrid();
            },
            (error) => {
                console.error('Error creating event:', error);
                // Auch bei Fehler Grid aktualisieren
                this.refreshGrid();
            },
        );
    }



    private handleEventCreationRequirements(eventId: number, formData: any) {
        // 1. Save Marke in events_characteristics
        this.saveEventCharacteristic(eventId, formData.marke);

        // 2. Create dynamic link to events_budgets with fixed budget at 100%
        this.createEventBudgetLink(eventId);

        // 3. Create up to 2 entries in events_costs (plan and actual) if values are filled
        this.createEventCostsIfNeeded(eventId, formData.plankosten, formData.istkosten);
    }

    private saveEventCharacteristic(eventId: number, marke: string) {
        if (!marke) return; // Only save if marke exists

        /**
         * This would need to use the actual characteristic service
         * For now, we'll use a placeholder approach
         */
        const characteristicData = {
            event_id: eventId,
            characteristic_value: marke,
        };

        /**
         * Implementation would depend on the actual characteristic service
         * For now, this is a placeholder
         */
    }

    private createEventBudgetLink(eventId: number) {
        /**
         * Get current user to determine budget allocation
         */
        const currentUser = this.userSettingsService.getValue('username') || 'User';

        /**
         * Placeholder for budget creation - this would need actual implementation
         * with the budget service to create a link to events_budgets
         */
        const budgetData = {
            event_id: eventId,
            user: currentUser,
            percentage: 100, // Fixed budget at 100%
        };

        /**
         * Implementation would depend on the actual budget service
         * For now, this is a placeholder
         */
    }

    private createEventCostsIfNeeded(eventId: number, planCost: any, actualCost: any) {
        // Create plan costs entry if value is filled
        if (planCost !== null && planCost !== undefined && planCost !== '') {
            const planCostData = {
                event_id: eventId,
                cost_type: 'plan',
                amount: planCost,
            };

            /**
             * Implementation would depend on the actual costs service
             * For now, this is a placeholder
             */
        }

        // Create actual costs entry if value is filled
        if (actualCost !== null && actualCost !== undefined && actualCost !== '') {
            const actualCostData = {
                event_id: eventId,
                cost_type: 'actual',
                amount: actualCost,
            };

            /**
             * Implementation would depend on the actual costs service
             * For now, this is a placeholder
             */
        }
    }

    closeEventForm() {
        this.showEventDetails = false;
        this.eventId = 0;
        this.eventForm.get('institution_name')?.enable();
        this.eventForm.reset();
        this.selectedInstitutionName = '';
    }

    private refreshGrid() {
        // Aktualisiert das Grid nach Änderungen
        if (this.institutionId) {
            this.loadData();
        }
    }



    // Event-Formular Submit Handler
    onEventSubmit(eventData: any) {
        const submitData = {
            id: eventData.id,
            institution_id: this.institutionId,
            event_type: eventData.eventData.event_type,
            event_subtype: eventData.eventData.event_subtype,
            event_status: eventData.eventData.event_status,
            event_start: eventData.eventData.event_start,
            event_end: eventData.eventData.event_end,
            brand: eventData.eventData.brand,
            event_cost_type_plan: eventData.eventData.event_cost_type_plan,
            event_cost_type_actual: eventData.eventData.event_cost_type_actual,
            cost_plan: eventData.eventData.cost_plan,
            cost_actual: eventData.eventData.cost_actual,
            event_notes: eventData.eventData.event_notes,
        };

        type BudgetAssignment = {budget_id?: number; region_id: string | number; budget_type: string | number};
        const assignmentsToValidate: BudgetAssignment[] = (eventData.budgetAssignments ?? []).filter(
            (a: BudgetAssignment) => Number(a.region_id) > 0,
        );

        if (assignmentsToValidate.length === 0) {
            this.performEventSave(submitData, eventData);
            return;
        }

        // Budget-Validierung VOR dem Speichern: erst prüfen, ob passendes Budget existiert
        forkJoin(
            assignmentsToValidate.map((assignment: BudgetAssignment) =>
                this.budgetAssignmentService.getRegionBudgetsForBudgetYear(
                    Number(assignment.region_id),
                    eventData.budgetYear,
                ).pipe(
                    map((result: CWResult) =>
                        (result?.data as SelectData[])?.find(
                            (b) => String(b.data?.budget_type) === String(assignment.budget_type),
                        ) ?? null,
                    ),
                ),
            ),
        ).subscribe({
            next: (matches: (SelectData | null)[]) => {
                if (matches.some((m: SelectData | null) => m === null)) {
                    this.showBudgetAssignmentError();
                    return;
                }
                this.performEventSave(submitData, eventData);
            },
            error: () => this.showBudgetAssignmentError(),
        });
    }

    private performEventSave(submitData: any, eventData: any) {
        const save$ = eventData.isNew ?
            this.eventsDataService.saveDataInstitutions(0, submitData) :
            this.eventsDataService.saveData(eventData.id, submitData);
        save$.subscribe({
            next: (result: any) => {
                if (result.success) {
                    const savedEventId: number = result.data?.id || eventData.id;
                    if (eventData.budgetAssignments?.length > 0) {
                        this.saveBudgetAssignments(savedEventId, eventData.budgetAssignments, eventData.budgetYear);
                    }
                    this.showEventDetails = false;
                    this.selectedEventData = null;
                    this.closeEventForm();
                    this.refreshGrid();
                } else if (!result.success && result.message.includes('Kein Budget')) {
                    this.showBudgetMissing();
                } else {
                    console.error('Error saving event:', result.message);
                }
            },
            error: (error) => console.error('Error saving event:', error),
        });
    }

    private saveBudgetAssignments(
        eventId: number,
        budgetAssignments: {budget_id?: number; region_id: string | number; budget_type: string | number}[],
        budgetYear: number,
    ) {
        for (const assignment of budgetAssignments) {
            if (Number(assignment.region_id) <= 0) {
                continue;
            }

            /*
             * Immer per aktuell gewählter Region + Budgetart nachschlagen,
             * damit Änderungen der Region im Formular korrekt übernommen werden.
             * Das Backend gibt seit dem Fix 'data.budget_type' im Suchergebnis zurück,
             * sodass der Vergleich zuverlässig funktioniert.
             */
            this.budgetAssignmentService.getRegionBudgetsForBudgetYear(
                Number(assignment.region_id),
                budgetYear,
            ).pipe(
                switchMap((result: CWResult) => {
                    const match = (result?.data as SelectData[])?.find(
                        /*
                         * String-Vergleich, da PHP und Angular unterschiedliche Typen
                         * zurückgeben können (z. B. int vs. string)
                         */
                        (b) => String(b.data?.budget_type) === String(assignment.budget_type),
                    );
                    if (!match) {
                        this.showBudgetAssignmentError();
                        return EMPTY;
                    }
                    return this.budgetAssignmentService.saveEventBudgetAssignments({
                        event_id: eventId,
                        budget_year: budgetYear,
                        assignments: [{
                            budget_id: match.id as number,
                            region_id: Number(assignment.region_id),
                            percentage: 100,
                        }],
                    });
                }),
            ).subscribe();
        }
    }

    /**
     * Öffnet den E-Detailer-Link
     */
    openEdetailer() {
        window.open(this.edetailerLink);
    }

    /**
     * Meldung, Einrichtung wird nicht angezeigt (andere Region oder OnlyOwnRegion-Premission)
     */
    showBudgetMissing() {
        // Dialog öffnen
        const dialogRef = this.dialog.open(PopupMessageComponent, {
            width: '350px',
            data: {
                title: 'Kein Budget vorhanden',
                message: 'Für die Region ist kein Budget vorhanden',
            },
        });
    }

    private showBudgetAssignmentError(): void {
        this.dialog.open(PopupMessageComponent, {
            width: '400px',
            data: {
                title: 'Budgetzuordnung fehlgeschlagen',
                message: 'Für das gewählte Gebiet und die Budgetart wurde kein passendes Budget gefunden. ' +
                    'Bitte prüfen Sie Gebiet und Budgetart und stellen Sie sicher, ' +
                    'dass für dieses Jahr ein Budget hinterlegt ist.',
            },
        });
    }

    /**
     * Checkbox-Selektion im Grid verarbeiten
     */
    onEventGridCheckboxClicked(event: CWEvent): void {
        this.checkedEvents = event.data.selection;
    }

    /**
     * Bestätigungsdialog zum Löschen der Veranstaltung öffnen
     */
    openDeleteDialog(): void {
        if (this.checkedEvents.length === 0) {
            return;
        }
        const dialogRef = this.dialog.open(PopupConfirmationComponent, {
            width: '350px',
            data: {
                title: 'Veranstaltung löschen',
                message: 'Soll die Veranstaltung wirklich gelöscht werden?',
            },
        });
        dialogRef.afterClosed().subscribe((result: {answer: string}) => this.deleteEvent(result));
    }

    /**
     * Veranstaltung löschen
     * @param result
     */
    deleteEvent(result: {answer: string}): void {
        if (result?.answer !== 'yes') {
            return;
        }
        forkJoin(
            this.checkedEvents.map((e) => this.eventsDataService.deleteData(e.id).pipe(catchError(() => of(null)))),
        ).subscribe(() => {
            this.checkedEvents = [];
            this.gridService.checkboxUncheckAll('institutionsEvents');
            this.eventId = 0;
            this.selectedEventData = null;
            this.refreshGrid();
        });
    }
}
